// SearchWorkflowContainer.tsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchWorkflow from "./SearchWorkflow";
import { getWorkflow } from "../../service/SearchWorkflowService";

/**
 * ワークフロー検索結果の各行データを表す型
 */
export type FilteredData = {
  id: string;
  mainName: string; // 大項目
  type: string;
  overview: string;
};

/**
 * ワークフロー検索コンテナコンポーネント
 * - ワークフロー検索の状態管理・検索・ソート・無限スクロール処理を行う
 * - 基本的に変数や関数はここで作成し、画面表示側（SearchWorkflow）に渡す
 */
export default function SearchWorkflowContainer() {
  // 全データを保持する状態
  const [allRecords, setAllRecords] = useState<FilteredData[]>([]);
  // 表示する記録
  const [visibleRecords, setVisibleRecords] = useState<FilteredData[]>([]);
  // ローディング中か否か
  const [loading, setLoading] = useState<boolean>(true);
  // すべての記録をローディング完了か否か
  const [allLoaded, setAllLoaded] = useState(false);
  // ローディングされた記録数
  const [loadedCount, setLoadedCount] = useState<number>(0);
  const navigate = useNavigate();
  // 順序並び方（sessionStorageから復元）
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(() => {
    const saved = sessionStorage.getItem("sortOrder_SearchWorkflow");
    return saved === "asc" || saved === "desc" ? saved : "asc";
  });
  // フィルター検索のテキスト（sessionStorageから復元）
  const [searchText, setSearchText] = useState(() => {
    return sessionStorage.getItem("searchText_SearchWorkflow") || "";
  });

  // Workflowのクリック処理
  const handleCardClick = useCallback(
    (id: string) => {
      navigate(`/CustomForm/${id}`);
    },
    [navigate]
  );

  // 申請書名でソートする関数(ソート順は申請書種別、申請書名、説明)
  const sortRecords = useCallback(
    (data: FilteredData[], order: "asc" | "desc") => {
      return [...data].sort((a, b) => {
        // typeで比較
        const typeCompare = a.type.localeCompare(b.type);
        if (typeCompare !== 0) {
          return order === "asc" ? typeCompare : -typeCompare;
        }

        // mainNameで比較
        const mainNameCompare = a.mainName.localeCompare(b.mainName);
        if (mainNameCompare !== 0) {
          return order === "asc" ? mainNameCompare : -mainNameCompare;
        }

        // overviewで比較
        const overviewCompare = a.overview.localeCompare(b.overview || "");
        return order === "asc" ? overviewCompare : -overviewCompare;
      });
    },
    []
  );

  // 検索処理する関数
  const filterRecords = useCallback(
    (data: FilteredData[], value: string): FilteredData[] => {
      if (!value.trim()) {
        return data;
      }
      return data.filter((item) => {
        const normalizedItem = (item.type + item.mainName + item.overview)
          .normalize("NFKC")
          .toLowerCase();
        const normalizedQuery = value.normalize("NFKC").toLowerCase();
        return normalizedItem.includes(normalizedQuery);
      });
    },
    []
  );

  // **1. サーバー通信処理と取得データの反映（初回のみ実行）**
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const data = await getWorkflow();
        setAllRecords(data);
      } catch (error) {
        console.error("Error fetching records:", error);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []); // 空の依存配列で初回のみ実行

  // **2. フィルタリング処理（searchTextまたはallRecordsの変化で再計算）**
  const filteredRecords = useMemo(() => {
    return filterRecords(allRecords, searchText);
  }, [allRecords, searchText, filterRecords]);

  // **3. ソート処理（filteredRecordsまたはsortOrderの変化で再計算）**
  const sortedFilteredRecords = useMemo(() => {
    return sortRecords(filteredRecords, sortOrder);
  }, [filteredRecords, sortOrder, sortRecords]);

  // **4. sortedFilteredRecordsの変化を監視して表示する記録を更新（分頁リセット）**
  useEffect(() => {
    const initialCount = 20;
    const initialRecords = sortedFilteredRecords.slice(0, initialCount);
    setVisibleRecords(initialRecords);
    setLoadedCount(initialRecords.length);
    setAllLoaded(initialRecords.length >= sortedFilteredRecords.length);
  }, [sortedFilteredRecords]);

  // **5. もっと多くの記録をローディング**
  const loadMoreRecords = useCallback(() => {
    if (loading || allLoaded) return;
    const nextCount = loadedCount + 5;
    const nextRecords = sortedFilteredRecords.slice(0, nextCount);
    setVisibleRecords(nextRecords);
    setLoadedCount(nextRecords.length);
    setAllLoaded(nextRecords.length >= sortedFilteredRecords.length);
  }, [loading, allLoaded, loadedCount, sortedFilteredRecords]);

  // **6. スクロールイベントを監視する**
  useEffect(() => {
    const handleScroll = () => {
      // 現在のスクロール位置とページの高さを取得する
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      if (scrollPosition >= documentHeight - 100) {
        // ページの底から100pxの位置で、さらに読み込む処理を実行する
        if (
          !loading &&
          visibleRecords.length < sortedFilteredRecords.length
        ) {
          loadMoreRecords();
        }
      }
    };

    // スクロールイベントを監視する
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loading, visibleRecords.length, sortedFilteredRecords.length, loadMoreRecords]);

  // ソートボタン押下時に実行する
  const toggleSortOrder = useCallback(() => {
    setSortOrder((prevOrder) => {
      const newOrder = prevOrder === "desc" ? "asc" : "desc";
      sessionStorage.setItem("sortOrder_SearchWorkflow", newOrder);
      return newOrder;
    });
  }, []);

  // テキストボックスのテキストをクリア
  const handleSearchTextClear = useCallback(() => {
    setSearchText("");
    sessionStorage.removeItem("searchText_SearchWorkflow");
  }, []);

  return (
    <SearchWorkflow
      filteredData={visibleRecords}
      allLoaded={allLoaded}
      loading={loading}
      handleCardClick={handleCardClick}
      sortOrder={sortOrder}
      searchText={searchText}
      setSearchText={setSearchText}
      toggleSortOrder={toggleSortOrder}
      handleSearchTextClear={handleSearchTextClear}
    />
  );
}
